import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import Cookies from 'js-cookie';
import { MessageListSkeleton } from '../components/Skeletons';
import '../styles/messageList.css';
import { UserContext } from '../contexts/UserContext';

const MessageList = () => {
  const navigate = useNavigate();
  const { redirectToLogin } = useContext(UserContext);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Fetch messages from API
  const fetchMessages = useCallback(async (page = 1, isRefresh = false) => {
    try {
      if (!isRefresh && page === 1) {
        setLoading(true);
      }
      if (page > 1) {
        setLoadingMore(true);
      }
      setError(null);

      const token = Cookies.get('authToken');
      if (!token) {
        redirectToLogin(window.location.pathname);
        return;
      }

      const response = await axios.get(
        `${config.backendUrl}/messages/`,
        {
          params: { page, page_size: 20 },
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (page === 1 || isRefresh) {
        setMessages(response.data.messages || []);
      } else {
        setMessages(prev => [...prev, ...(response.data.messages || [])]);
      }

      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching messages:', error);
      if (error.response?.status === 401) {
        setError('Session expirée. Veuillez vous reconnecter.');
        setTimeout(() => redirectToLogin('/login'), 2000);
      } else {
        setError(error.response?.data?.error || 'Erreur lors du chargement des messages');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    fetchMessages(1, true);
  }, [fetchMessages]);

  const handleLoadMore = useCallback(() => {
    if (pagination?.has_next && !loadingMore) {
      fetchMessages(pagination.current_page + 1);
    }
  }, [pagination, loadingMore, fetchMessages]);

  const handleMessageClick = useCallback((messageId) => {
    navigate(`/sms-history/${messageId}`);
  }, [navigate]);

  const handleCreateMessage = useCallback(() => {
    navigate('/communication');
  }, [navigate]);

  const formatDate = useCallback((dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  }, []);

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'PENDING':
        return '#FFA500'; // Orange
      case 'SENDING':
      case 'IN_PROGRESS':
        return '#003366'; // Brand blue
      case 'COMPLETED':
        return '#28A745'; // Green
      case 'FAILED':
        return '#c51618'; // Brand red
      case 'CANCELLED':
        return '#6C757D'; // Gray
      default:
        return '#6C757D';
    }
  }, []);

  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case 'PENDING':
        return 'fa-clock';
      case 'SENDING':
      case 'IN_PROGRESS':
        return 'fa-paper-plane';
      case 'COMPLETED':
        return 'fa-check-circle';
      case 'FAILED':
        return 'fa-times-circle';
      case 'CANCELLED':
        return 'fa-stop-circle';
      default:
        return 'fa-question-circle';
    }
  }, []);

  const truncateMessage = useCallback((message, maxLength = 100) => {
    if (!message) return '';
    return message.length > maxLength ? `${message.substring(0, maxLength)}...` : message;
  }, []);

  const renderMessageItem = useCallback((item) => {
    const statusColor = getStatusColor(item.status);
    const statusIcon = getStatusIcon(item.status);
    const deliveryRate = item.recipient_count > 0 
      ? Math.round((item.sent_count / item.recipient_count) * 100) 
      : 0;

    return (
      <div
        key={item.id}
        className="message-card"
        onClick={() => handleMessageClick(item.id)}
      >
        <div className="message-card-header">
          <div className="message-status" style={{ backgroundColor: statusColor }}>
            <i className={`fas ${statusIcon}`}></i>
            <span>{item.status_display}</span>
          </div>
          <span className="message-date">{formatDate(item.created_at)}</span>
        </div>

        <div className="message-content">
          <p className="message-text">{truncateMessage(item.message)}</p>
        </div>

        <div className="message-stats">
          <div className="stat-item">
            <i className="fas fa-users"></i>
            <span>{item.recipient_count} destinataires</span>
          </div>
          <div className="stat-item">
            <i className="fas fa-check"></i>
            <span>{item.sent_count} envoyés</span>
          </div>
          {item.failed_count > 0 && (
            <div className="stat-item error">
              <i className="fas fa-times"></i>
              <span>{item.failed_count} échecs</span>
            </div>
          )}
        </div>

        {item.status === 'COMPLETED' && (
          <div className="delivery-rate">
            <div className="delivery-rate-bar">
              <div 
                className="delivery-rate-fill" 
                style={{ width: `${deliveryRate}%`, backgroundColor: statusColor }}
              ></div>
            </div>
            <span className="delivery-rate-text">{deliveryRate}% livré</span>
          </div>
        )}

        <div className="message-cost">
          <i className="fas fa-coins"></i>
          <span>{item.total_cost} Zaires</span>
        </div>
      </div>
    );
  }, [getStatusColor, getStatusIcon, formatDate, truncateMessage, handleMessageClick]);

  const renderEmptyState = () => (
    <div className="empty-state">
      <i className="fas fa-envelope-open-text empty-icon"></i>
      <h3>Aucun message</h3>
      <p>Vous n'avez pas encore envoyé de messages à vos followers.</p>
      <button className="btn-primary" onClick={handleCreateMessage}>
        Envoyer un message
      </button>
    </div>
  );

  if (loading && !refreshing) {
    return (
      <div className="message-list-container">
        <MessageListSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="message-list-container">
        <div className="error-container">
          <i className="fas fa-exclamation-triangle error-icon"></i>
          <h3>Erreur</h3>
          <p>{error}</p>
          <button className="btn-retry" onClick={handleRefresh}>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="message-list-container">
      {/* Header */}
      <div className="message-list-header">
        <div className="header-content">
          <h1>Mes Messages</h1>
          <p>Historique des messages envoyés</p>
        </div>
        <button className="btn-create" onClick={handleCreateMessage}>
          <i className="fas fa-plus"></i>
        </button>
      </div>

      {/* Messages List */}
      <div className="messages-grid">
        {messages.length === 0 ? (
          renderEmptyState()
        ) : (
          <>
            {messages.map(renderMessageItem)}
            
            {/* Load More Button */}
            {pagination?.has_next && (
              <div className="load-more-container">
                <button 
                  className="btn-load-more" 
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <div className="spinner-small"></div>
                      <span>Chargement...</span>
                    </>
                  ) : (
                    'Charger plus'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination Info */}
      {pagination && messages.length > 0 && (
        <div className="pagination-info">
          <span>
            {pagination.current_page} sur {pagination.total_pages} pages
          </span>
          <span className="separator">•</span>
          <span>
            {pagination.total_count} message{pagination.total_count > 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Refresh Button (floating) */}
      <button 
        className={`btn-refresh ${refreshing ? 'spinning' : ''}`}
        onClick={handleRefresh}
        disabled={refreshing}
        title="Actualiser"
      >
        <i className="fas fa-sync-alt"></i>
      </button>
    </div>
  );
};

export default MessageList;
